// src/components/tutorials/JS_Array_Search.js
import React, { useRef } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button
function TryItButton({ code }) {
  const handleClick = () => {
    const encodedCode = encodeURIComponent(code);
    window.open(`/editorJS?code=${encodedCode}`, "_blank");
  };

  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

// 🔹 Random border color generator
function getRandomColor() {
  const colors = [
    "#2563eb",
    "#16a34a",
    "#dc2626",
    "#d97706",
    "#9333ea",
    "#0891b2",
    "#f59e0b",
    "#e11d48",
    "#0ea5e9",
    "#84cc16",
  ];
  return colors[Math.floor(Math.random() * colors.length)];
}

// 🔹 Output Box Component
function OutputBox({ children }) {
  const borderColor = getRandomColor();
  return (
    <div
      className="output-box"
      style={{
        border: `2px dashed ${borderColor}`,
        transition: "border-color 0.3s ease",
      }}
    >
      {children}
    </div>
  );
}

export default function JS_Array_Search() {
  const refs = {
    indexOf: useRef(null),
    lastIndexOf: useRef(null),
    includes: useRef(null),
    find_findIndex: useRef(null),
    filter: useRef(null),
  };

  const scrollTo = (key) => {
    const r = refs[key];
    if (r && r.current) {
      r.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const keyPoints = [
    { key: "indexOf", label: "indexOf()" },
    { key: "lastIndexOf", label: "lastIndexOf()" },
    { key: "includes", label: "includes()" },
    { key: "find_findIndex", label: "find() & findIndex()" },
    { key: "filter", label: "filter()" },
  ];

  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">JavaScript Array Searching Methods</h1>

      <p className="tutorial-intro">
        JavaScript provides several built-in methods to search, locate, and
        filter elements in arrays. These methods help in checking existence,
        finding positions, or even extracting specific values that match certain
        conditions.
      </p>

      {/* Key Points Section */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0fdf4, #bbf7d0)",
        }}
      >
        <h1 style={{ color: "#166534", fontSize: "2.4rem", margin: 0 }}>
          Array Search Concepts & Examples
        </h1>
        <p style={{ fontSize: "1.05rem", marginTop: "0.5rem" }}>
          Click a topic below to explore examples and test them interactively!
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            marginTop: "1.25rem",
          }}
        >
          {keyPoints.map((l) => (
            <p key={l.key} style={{ margin: "0.25rem 0" }}>
              <button
                onClick={() => scrollTo(l.key)}
                style={{
                  background: "transparent",
                  border: "none",
                  textDecoration: "underline",
                  color: "#000",
                  cursor: "pointer",
                  fontSize: "1.05rem",
                }}
              >
                {l.label}
              </button>
            </p>
          ))}
        </div>
      </div>

      {/* 1️⃣ indexOf() */}
      <div ref={refs.indexOf} className="tutorial-section">
        <h2 className="section-title">1️⃣ indexOf()</h2>
        <p>
          The <code className="highlight_code">indexOf()</code> method returns
          the <b>first index</b> at which a given element is found, or{" "}
          <code className="highlight_code">-1</code> if it is not present.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let fruits = ["Apple", "Banana", "Cherry", "Banana"];
console.log(fruits.indexOf("Banana")); // 1
console.log(fruits.indexOf("Mango")); // -1`}
          />
          <TryItButton
            code={`let fruits = ["Apple", "Banana", "Cherry", "Banana"];
console.log(fruits.indexOf("Banana"));
console.log(fruits.indexOf("Mango"));`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>1 -1</pre>
        </OutputBox>
      </div>

      {/* 2️⃣ lastIndexOf() */}
      <div ref={refs.lastIndexOf} className="tutorial-section">
        <h2 className="section-title">2️⃣ lastIndexOf()</h2>
        <p>
          The <code className="highlight_code">lastIndexOf()</code> method
          returns the <b>last index</b> at which a given element appears in the
          array.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let fruits = ["Apple", "Banana", "Cherry", "Banana"];
console.log(fruits.lastIndexOf("Banana")); // 3`}
          />
          <TryItButton
            code={`let fruits = ["Apple", "Banana", "Cherry", "Banana"];
console.log(fruits.lastIndexOf("Banana"));`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>3</pre>
        </OutputBox>
      </div>

      {/* 3️⃣ includes() */}
      <div ref={refs.includes} className="tutorial-section">
        <h2 className="section-title">3️⃣ includes()</h2>
        <p>
          The <code className="highlight_code">includes()</code> method checks
          if an array contains a specific element and returns{" "}
          <code className="highlight_code">true</code> or{" "}
          <code className="highlight_code">false</code>.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let fruits = ["Apple", "Banana", "Cherry"];
console.log(fruits.includes("Banana")); // true
console.log(fruits.includes("Mango")); // false`}
          />
          <TryItButton
            code={`let fruits = ["Apple", "Banana", "Cherry"];
console.log(fruits.includes("Banana"));
console.log(fruits.includes("Mango"));`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>true false</pre>
        </OutputBox>
      </div>

      {/* 4️⃣ find() & findIndex() */}
      <div ref={refs.find_findIndex} className="tutorial-section">
        <h2 className="section-title">4️⃣ find() & findIndex()</h2>
        <p>
          These methods test each element with a callback function:
          <br />• <code className="highlight_code">find()</code> returns the{" "}
          <b>first matching value</b>.
          <br />• <code className="highlight_code">findIndex()</code> returns
          the <b>index</b> of the first matching value.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let numbers = [10, 25, 40, 55];
console.log(numbers.find(n => n > 30)); // 40
console.log(numbers.findIndex(n => n > 30)); // 2`}
          />
          <TryItButton
            code={`let numbers = [10, 25, 40, 55];
console.log(numbers.find(n => n > 30));
console.log(numbers.findIndex(n => n > 30));`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>40 2</pre>
        </OutputBox>
      </div>

      {/* 5️⃣ filter() */}
      <div ref={refs.filter} className="tutorial-section">
        <h2 className="section-title">5️⃣ filter()</h2>
        <p>
          The <code className="highlight_code">filter()</code> method creates a{" "}
          <b>new array</b> containing elements that satisfy a provided
          condition.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let numbers = [10, 25, 40, 55];
let result = numbers.filter(n => n > 30);
console.log(result); // [40, 55]`}
          />
          <TryItButton
            code={`let numbers = [10, 25, 40, 55];
let result = numbers.filter(n => n > 30);
console.log(result);`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>[40, 55]</pre>
        </OutputBox>
      </div>

      {/* Footer */}
      <div
        style={{
          background: "linear-gradient(135deg, #f0fdf4, #bbf7d0)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#166534" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Try combining <code className="highlight_code">filter()</code> and{" "}
            <code className="highlight_code">map()</code> for data
            transformations.
          </li>
          <li>
            Learn about <code className="highlight_code">some()</code> and{" "}
            <code className="highlight_code">every()</code> for condition
            checking.
          </li>
          <li>
            Experiment with <code className="highlight_code">find()</code>{" "}
            inside nested array structures.
          </li>
        </ul>
      </div>
    </div>
  );
}
